import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Clock, Calendar, MailOpen, Edit, Trash, Download, Plus, Save, Mail } from 'lucide-react';
import { useDocuments } from '../context/DocumentsContext';
import { useClients } from '../context/ClientsContext';
import { useAuth } from '../context/AuthContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Select from '../components/UI/Select';
import Input from '../components/UI/Input';
import TextArea from '../components/UI/TextArea';
import EmailDocumentModal from '../components/UI/EmailDocumentModal';

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

const DocumentPreview: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { documents, getDocument, updateDocument, deleteDocument } = useDocuments();
  const { getClient } = useClients();
  const { user } = useAuth();
  
  const document = id ? getDocument(id) : null;
  const client = document ? getClient(document.client_id) : null;

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [status, setStatus] = useState(document?.status || 'draft');
  const [content, setContent] = useState(document?.content || '');
  const [lineItems, setLineItems] = useState<LineItem[]>(
    document?.line_items || [{
      description: document?.prompt || '',
      quantity: 1,
      unitPrice: document?.amount || 0,
      total: document?.amount || 0
    }]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');

  if (!document || !client || !user) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="h-12 w-12 text-warning-500 mb-4" />
        <h2 className="text-2xl font-bold">Document Non Trouvé</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Le document que vous recherchez n'existe pas ou a été supprimé.
        </p>
        <Button 
          variant="outline" 
          className="mt-6"
          onClick={() => navigate('/app/documents')}
          leftIcon={<ArrowLeft className="h-4 w-4" />}
        >
          Retour aux Documents
        </Button>
      </div>
    );
  }

  const handleDownloadPDF = async () => {
    if (typeof window === 'undefined') {
      setError('Cette fonctionnalité n\'est disponible que dans un navigateur');
      return;
    }

    try {
      setIsDownloading(true);
      setError('');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf?id=${document.id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la génération du PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a hidden anchor element for downloading
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.type === 'invoice' ? 'Facture' : 'Devis'}_${document.number}.pdf`;
      
      // Trigger download
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Une erreur est survenue lors du téléchargement du PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[index] };

    if (field === 'description') {
      item.description = value;
    } else {
      const numValue = parseFloat(value) || 0;
      if (field === 'quantity' || field === 'unitPrice') {
        item[field] = numValue;
        item.total = item.quantity * item.unitPrice;
      }
    }

    newLineItems[index] = item;
    setLineItems(newLineItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      const newLineItems = lineItems.filter((_, i) => i !== index);
      setLineItems(newLineItems);
    }
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    if (!document.vat) return subtotal;
    const vatAmount = subtotal * (document.vat_rate / 100);
    return subtotal + vatAmount;
  };

  const handleStatusChange = async (newStatus: Document['status']) => {
    setStatus(newStatus);
    try {
      await updateDocument(document.id, { status: newStatus });
    } catch (error) {
      console.error('Error updating status:', error);
      setStatus(document.status);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      const subtotal = calculateSubtotal();
      await updateDocument(document.id, {
        content,
        status,
        amount: subtotal,
        line_items: lineItems
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving document:', error);
      setError('Une erreur est survenue lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDocument(document.id);
      navigate('/app/documents');
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            className="mr-2"
            onClick={() => navigate('/app/documents')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {document.type === 'invoice' ? 'Facture' : 'Devis'} #{document.number}
            </h1>
            <div className="flex items-center mt-1">
              <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${
                status === 'draft' ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' :
                status === 'sent' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                status === 'paid' ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300' :
                'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              }`}>
                {status === 'draft' ? <Clock className="h-4 w-4 mr-1" /> :
                 status === 'sent' ? <MailOpen className="h-4 w-4 mr-1" /> :
                 status === 'paid' ? <FileText className="h-4 w-4 mr-1" /> :
                 <AlertTriangle className="h-4 w-4 mr-1" />}
                {status === 'draft' ? 'Brouillon' :
                 status === 'sent' ? 'Envoyé' :
                 status === 'paid' ? 'Payé' :
                 'Annulé'}
              </div>
              <span className="mx-2 text-gray-400">•</span>
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                <Calendar className="inline h-4 w-4 mr-1 -mt-0.5" />
                {new Date(document.date).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!showDeleteConfirm && (
            <>
              <Select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value as Document['status'])}
                options={[
                  { value: 'draft', label: 'Brouillon' },
                  { value: 'sent', label: 'Envoyé' },
                  { value: 'paid', label: 'Payé' },
                  { value: 'cancelled', label: 'Annulé' },
                ]}
                className="w-32"
              />

              <Button 
                variant="outline"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleDownloadPDF}
                disabled={isEditing || isDownloading}
                isLoading={isDownloading}
              >
                {isDownloading ? 'Téléchargement...' : 'Télécharger'}
              </Button>

              <Button 
                variant="outline"
                leftIcon={<Mail className="h-4 w-4" />}
                onClick={() => setShowEmailModal(true)}
                disabled={isEditing}
              >
                Envoyer
              </Button>
              
              {isEditing ? (
                <Button 
                  variant="primary"
                  leftIcon={<Save className="h-4 w-4" />}
                  onClick={handleSave}
                  isLoading={isSaving}
                >
                  Enregistrer
                </Button>
              ) : (
                <Button 
                  variant="outline"
                  leftIcon={<Edit className="h-4 w-4" />}
                  onClick={() => setIsEditing(true)}
                >
                  Modifier
                </Button>
              )}
              
              <Button 
                variant="outline"
                leftIcon={<Trash className="h-4 w-4" />}
                className="text-error-500 border-error-300 hover:bg-error-50 dark:border-error-700 dark:hover:bg-error-900/20"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isEditing}
              >
                Supprimer
              </Button>
            </>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <Card className="border-error-300 dark:border-error-700">
          <Card.Content className="space-y-4">
            <div className="flex items-start">
              <div className="mr-4 p-2 rounded-full bg-error-100 dark:bg-error-900/20 text-error-500">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Supprimer le Document</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Êtes-vous sûr de vouloir supprimer {document.type === 'invoice' ? 'la facture' : 'le devis'} #{document.number} ? Cette action est irréversible.
                </p>
              </div>
            </div>
          </Card.Content>
          
          <Card.Footer className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
            >
              Annuler
            </Button>
            <Button 
              variant="outline"
              className="text-error-500 border-error-300 hover:bg-error-50 dark:border-error-700 dark:hover:bg-error-900/20"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </Card.Footer>
        </Card>
      )}

      {error && (
        <div className="bg-error-50 dark:bg-error-900/20 p-4 rounded-md border border-error-200 dark:border-error-800">
          <p className="text-error-600 dark:text-error-400">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-center">
              {user?.logo ? (
                <img 
                  src={user.logo} 
                  alt="Logo de l'entreprise" 
                  className="h-16 w-auto object-contain mr-4"
                />
              ) : (
                <h2 className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {user.company || user.name}
                </h2>
              )}
            </div>
            <div className="text-right">
              <h3 className="text-xl font-bold">{document.type === 'invoice' ? 'FACTURE' : 'DEVIS'}</h3>
              <p>#{document.number}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">De</h4>
              <p className="font-medium">{user.company || user.name}</p>
              <p>{user.address}</p>
              <p>{user.postal_code} {user.city}</p>
              <p>{user.country}</p>
              {user.siret && <p>SIRET : {user.siret}</p>}
              {user.vat_number && <p>TVA : {user.vat_number}</p>}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Facturer à</h4>
              <p className="font-medium">{client.name}</p>
              {client.company && <p>{client.company}</p>}
              <p>{client.address}</p>
              <p>{client.email}</p>
              <p>{client.phone}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Date</h4>
              <p>{new Date(document.date).toLocaleDateString()}</p>
            </div>
            {document.type === 'invoice' && document.due_date && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Échéance</h4>
                <p>{new Date(document.due_date).toLocaleDateString()}</p>
              </div>
            )}
          </div>
          
          <div className="mb-8">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-t-lg border border-gray-200 dark:border-gray-600 px-4 py-3">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <h4 className="text-sm font-medium">Description</h4>
                </div>
                <div className="col-span-2 text-right">
                  <h4 className="text-sm font-medium">Quantité</h4>
                </div>
                <div className="col-span-2 text-right">
                  <h4 className="text-sm font-medium">Prix unitaire</h4>
                </div>
                <div className="col-span-2 text-right">
                  <h4 className="text-sm font-medium">Total</h4>
                </div>
              </div>
            </div>

            <div className="border-x border-b border-gray-200 dark:border-gray-600 rounded-b-lg">
              {lineItems.map((item, index) => (
                <div key={index} className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-6">
                      {isEditing ? (
                        <TextArea
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          rows={2}
                        />
                      ) : (
                        <p className="whitespace-pre-wrap">{item.description}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      {isEditing ? (
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                          className="text-right"
                          min="1"
                        />
                      ) : (
                        <p className="text-right">{item.quantity}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      {isEditing ? (
                        <Input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                          className="text-right"
                          min="0"
                          step="0.01"
                        />
                      ) : (
                        <p className="text-right">{item.unitPrice.toLocaleString()} {user.currency_symbol}</p>
                      )}
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end">
                      <span className="mr-2">{item.total.toLocaleString()} {user.currency_symbol}</span>
                      {isEditing && lineItems.length > 1 && (
                        <button
                          onClick={() => removeLineItem(index)}
                          className="text-error-500 hover:text-error-600 dark:text-error-400 dark:hover:text-error-300"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {isEditing && (
                <div className="px-4 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    leftIcon={<Plus className="h-4 w-4" />}
                  >
                    Ajouter une ligne
                  </Button>
                </div>
              )}

              <div className="px-4 py-4">
                <div className="flex justify-end">
                  <div className="w-48">
                    <div className="flex justify-between py-1">
                      <p className="text-gray-600 dark:text-gray-400">Montant HT</p>
                      <p>{calculateSubtotal().toLocaleString()} {user.currency_symbol}</p>
                    </div>
                    {document.vat && (
                      <div className="flex justify-between py-1">
                        <p className="text-gray-600 dark:text-gray-400">TVA ({document.vat_rate}%)</p>
                        <p>{(calculateSubtotal() * document.vat_rate / 100).toLocaleString()} {user.currency_symbol}</p>
                      </div>
                    )}
                    <div className="flex justify-between py-1 font-medium border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                      <p>Total {document.vat ? 'TTC' : 'HT'}</p>
                      <p>{calculateTotal().toLocaleString()} {user.currency_symbol}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Notes</h4>
            {isEditing ? (
              <TextArea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
              />
            ) : (
              <div className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">
                {content}
              </div>
            )}
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Merci pour votre confiance !
            </p>
          </div>
        </div>
      </div>

      {showEmailModal && (
        <EmailDocumentModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          documentNumber={document.number}
          documentType={document.type}
          clientEmail={client.email}
          clientName={client.name}
          onSuccess={() => {
            handleStatusChange('sent');
            setShowEmailModal(false);
          }}
          documentId={document.id}
        />
      )}
    </div>
  );
};

export default DocumentPreview;